/**
 * Sector benchmarks for carbon intensity metrics.
 *
 * Keys are NACE sector codes (A–U), matching the `assessment.sector` field.
 *
 * Units:
 *   revenueIntensity  — tCO2e per 1,000 revenue units (currency-agnostic approximation)
 *   employeeIntensity — tCO2e per employee (FTE)
 *
 * Values are approximate global SME averages drawn from publicly available sources.
 * Sectors T and U are omitted — no reliable public benchmark data exists for them.
 */

export interface SectorBenchmark {
  revenueIntensity: number | null; // tCO2e per 1,000 revenue units
  employeeIntensity: number | null; // tCO2e per employee
  source: string; // e.g. "CDP 2023 Global SME Average"
}

export const SECTOR_BENCHMARKS: Record<string, SectorBenchmark> = {
  // A — Agriculture, forestry and fishing
  // High land-use emissions; IEA/FAO global averages for SME farms & forestry operations
  A: {
    revenueIntensity: 2.5,
    employeeIntensity: 35.0,
    source: "IEA / FAO 2023",
  },

  // B — Mining and quarrying
  // Scope 1–2 dominated by on-site fuel combustion and electricity; CDP mining sector report
  B: {
    revenueIntensity: 0.85,
    employeeIntensity: 45.0,
    source: "CDP 2023 Mining & Metals",
  },

  // C — Manufacturing
  // Broad average across light and heavy manufacturing sub-sectors
  C: {
    revenueIntensity: 0.65,
    employeeIntensity: 18.0,
    source: "CDP 2023 Global SME Average",
  },

  // D — Electricity, gas, steam and air conditioning supply
  // Inherently high — generation and distribution losses dominate
  D: {
    revenueIntensity: 3.2,
    employeeIntensity: 120.0,
    source: "IEA 2023 Energy Sector",
  },

  // E — Water supply; sewerage, waste management and remediation
  // Methane from waste treatment is the main driver; CDP environmental services data
  E: {
    revenueIntensity: 0.45,
    employeeIntensity: 12.0,
    source: "CDP 2023 Water & Waste Utilities",
  },

  // F — Construction
  // Scope 1 fuel + Scope 3 materials dominate; CDP construction sector report
  F: {
    revenueIntensity: 0.18,
    employeeIntensity: 8.5,
    source: "CDP 2023 Construction",
  },

  // G — Wholesale and retail trade; repair of motor vehicles
  // Primarily Scope 3 (supply chain); own operations are relatively lean
  G: {
    revenueIntensity: 0.08,
    employeeIntensity: 4.2,
    source: "CDP 2023 Retail & Wholesale",
  },

  // H — Transportation and storage
  // Fuel combustion is the primary source; IEA transport statistics
  H: {
    revenueIntensity: 0.55,
    employeeIntensity: 22.0,
    source: "IEA 2023 Transport",
  },

  // I — Accommodation and food service activities
  // Energy for buildings + refrigerants; CDP hospitality sector data
  I: {
    revenueIntensity: 0.12,
    employeeIntensity: 5.8,
    source: "CDP 2023 Hospitality & Food Service",
  },

  // J — Information and communication (Technology)
  // Data-centre electricity dominates; CDP technology sector report
  J: {
    revenueIntensity: 0.04,
    employeeIntensity: 3.2,
    source: "CDP 2023 Technology & Telecoms",
  },

  // K — Financial and insurance activities
  // Office operations only; financed emissions (Scope 3 Cat 15) excluded here
  K: {
    revenueIntensity: 0.02,
    employeeIntensity: 2.8,
    source: "CDP 2023 Financial Services",
  },

  // L — Real estate activities
  // Landlord-controlled energy; GRESB / CDP real estate benchmark
  L: {
    revenueIntensity: 0.10,
    employeeIntensity: 6.5,
    source: "CDP 2023 Real Estate",
  },

  // M — Professional, scientific and technical activities
  // Office-based; air travel is the main variable; CDP professional services
  M: {
    revenueIntensity: 0.03,
    employeeIntensity: 2.1,
    source: "CDP 2023 Professional Services",
  },

  // N — Administrative and support service activities
  // Similar profile to M; facility management adds marginal fuel/electricity
  N: {
    revenueIntensity: 0.04,
    employeeIntensity: 3.8,
    source: "CDP 2023 Business Services",
  },

  // O — Public administration and defence
  // Revenue metric not meaningful for government; employee intensity from IEA public sector data
  O: {
    revenueIntensity: null,
    employeeIntensity: 5.5,
    source: "IEA 2023 Public Sector",
  },

  // P — Education
  // Campus energy + commuting; CDP education sector approximation
  P: {
    revenueIntensity: 0.03,
    employeeIntensity: 2.5,
    source: "CDP 2023 Education",
  },

  // Q — Human health and social work activities
  // Hospitals / clinics: energy, anaesthetic gases, waste; CDP healthcare data
  Q: {
    revenueIntensity: 0.05,
    employeeIntensity: 3.5,
    source: "CDP 2023 Healthcare",
  },

  // R — Arts, entertainment and recreation
  // Events / venues; energy and travel-heavy; CDP services approximation
  R: {
    revenueIntensity: 0.06,
    employeeIntensity: 4.5,
    source: "CDP 2023 Services Sector",
  },

  // S — Other service activities
  // Mixed (beauty, repair, personal services); CDP services approximation
  S: {
    revenueIntensity: 0.04,
    employeeIntensity: 3.0,
    source: "CDP 2023 Services Sector",
  },

  // T — Activities of households as employers / undifferentiated household production
  // No reliable public benchmark data; omitted intentionally
  // U — Activities of extraterritorial organisations and bodies
  // No reliable public benchmark data; omitted intentionally
};

export function getBenchmark(sector: string): SectorBenchmark | null {
  return SECTOR_BENCHMARKS[sector] ?? null;
}
